import { AuthViewProps } from "@homebound/auth-components";
import {
  BeamProvider,
  Css,
  PreventBrowserScroll,
  ScrollableParent,
  setGridTableDefaults,
  Toast,
} from "@homebound/beam";
import { PropsWithChildren, useEffect } from "react";
import { NavigationContextProvider } from "src/components";
import { DownloadTracker } from "src/components/DownloadTracker";
import { ErrorBoundary } from "src/components/ErrorBoundary";
import { LocalProdEnvironmentWarning } from "src/components/LocalProdEnvironmentWarning";
import { ZendeskWidget } from "src/components/ZendeskWidget";
import { getStage } from "src/context";
import { AppNotificationsProvider } from "src/contexts/AppNotificationsContext";
import { BlueprintProvider } from "src/contexts/BlueprintContext";
import { FeatureFlagProvider } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { TrackedJobsProvider } from "src/contexts/TrackedJobsContext";
import { useMatchAny } from "src/hooks/useMatchAny";
import { useCurrentInternalUserQuery } from "src/queries/user.generated";
import { GlobalNav } from "src/routes/layout/GlobalNav";
import {
  adminPaths,
  changeLogPaths,
  developmentPaths,
  librariesPath,
  personalDashboardPaths,
  projectPaths,
  tradeCategoryLeadTimesPath,
  warrantyPaths,
} from "src/routes/routesDef";
import { setAnalyticsUser } from "src/utils/analytics";
import { showIdColumn } from "src/utils/idColumn";
import { LocalCssReset } from "src/utils/LocalCssReset";
import { ImpersonationWarning, impersonationWarningStickyTopPx } from "./ImpersonationWarning";
import { QaEnvironmentWarning } from "./QaEnvironmentWarning";

export type PageLayoutProps = PropsWithChildren<Pick<AuthViewProps, "user" | "setIsAuthenticated">>;

/** Wraps the application pages (passed as children) with our logged-in-user layout/nav. */
export function PageLayout(props: PageLayoutProps) {
  const { data } = useCurrentInternalUserQuery();
  const currentInternalUser = data?.currentInternalUser;
  const internalUserImpersonation = data?.internalUserImpersonation;
  const { id, name, email } = currentInternalUser || {};

  // Apply grid table defaults for empty cells
  setGridTableDefaults({ stickyHeader: true, style: { allWhite: true } });

  useEffect(() => {
    setAnalyticsUser({ id, name, email });
    // Always show the debugging `ID` column
    if (email?.includes("shaberman")) {
      showIdColumn();
    }
  }, [id, name, email]);

  // We want two separate top level layouts whether you are in a Project/Development/Libraries specific path or not.
  // This makes styling with the side nav easier if we do not need to nest ScrollableParent containers.
  const hasOwnLayout = useMatchAny([
    projectPaths.project,
    developmentPaths.development,
    adminPaths.admin,
    librariesPath,
    personalDashboardPaths.dashboard,
    tradeCategoryLeadTimesPath,
    changeLogPaths.base,
    warrantyPaths.details,
  ]);

  const content = hasOwnLayout ? (
    props.children
  ) : (
    <ScrollableParent tagName="main" px={3} xss={Css.bgGray100.$}>
      {props.children}
    </ScrollableParent>
  );

  const stage = getStage();

  return (
    <BeamProvider>
      <LocalCssReset />
      <NavigationContextProvider>
        <BlueprintProvider>
          <FeatureFlagProvider>
            <AppNotificationsProvider>
              <TrackedJobsProvider>
                <ZendeskWidget />
                <PreventBrowserScroll>
                  <GlobalNav user={props.user} />
                  {stage === "local-prod" && <LocalProdEnvironmentWarning />}
                  {stage === "qa" && <QaEnvironmentWarning />}
                  {internalUserImpersonation && (
                    <ImpersonationWarning
                      impersonatee={internalUserImpersonation.impersonatee}
                      stickyTopPx={impersonationWarningStickyTopPx()}
                    />
                  )}
                  <Toast />
                  <DownloadTracker />
                  <ErrorBoundary>{content}</ErrorBoundary>
                </PreventBrowserScroll>
              </TrackedJobsProvider>
            </AppNotificationsProvider>
          </FeatureFlagProvider>
        </BlueprintProvider>
      </NavigationContextProvider>
    </BeamProvider>
  );
}
